import cn from 'classnames';
import { useComponentInViewport } from 'core-lib/hooks/useComponentInViewport';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from 'ui-v2/components/Typography';
import { Button } from 'ui-v2/components/Button';
import useStoreItemClick from '../../../hooks/useStoreItemClick';
import { LayoutEntityType, LayoutPropType } from '../../../types/layout';
import {
  trackCardClickedStoreV2,
  trackCtaClickedStoreV2,
  trackSectionViewedStoreV2,
} from '../../../utils/events';
import NewLaunchCard from '../../Cards/NewLaunchCard';
import s from './FilterScrollListing.module.scss';
import Icon from 'ui-v2/components/Icon/Icon';
import AnimatedImage from 'core-lib/components/AnimatedImage';

const parentIdMap = {};

export const FilterScrollListing = ({ data }: LayoutPropType) => {
  // state
  const [activeTab, setActiveTab] = useState(0);

  const { handleItemClick, handleItemClickWithDelegation } =
    useStoreItemClick();
  const CARDID = data.entityType || 'FilterScrollListing';

  //const
  const rootRef = useRef();
  const isEventFired = useRef(false);
  const inViewPort = useComponentInViewport(rootRef, {
    threshold: 0.5,
  });

  useEffect(() => {
    if (inViewPort && !isEventFired.current) {
      trackSectionViewedStoreV2(data?.title);
      isEventFired.current = true;
    }
  }, [data?.title, inViewPort]);

  useMemo(() => {
    data?.metaData?.categories.forEach((item) => {
      parentIdMap[item?.productId] = [];
    });
    data?.data?.forEach((item) => {
      item?.parentId && parentIdMap?.[item?.parentId]?.push(item);
    });
  }, [data]);

  const getIndex = (id: number) =>
    data.data.findIndex((item) => item.productId === id);

  const handleTabClick = (idx) => {
    setActiveTab(idx);
    const ele = document.getElementById(`${data?.layoutId}-${idx}-brandsTab&`);
    const productEle = document.getElementById(
      `${data?.layoutId}-productCard&`
    );
    if (ele) {
      const timeout = setTimeout(() => {
        ele.scrollIntoView({
          block: 'nearest',
          inline: 'center',
          behavior: 'smooth',
        });
        clearTimeout(timeout);
      }, 100);
    }

    if (productEle) {
      const timeout = setTimeout(() => {
        productEle.scrollIntoView({
          block: 'nearest',
          inline: 'center',
          behavior: 'smooth',
        });
        clearTimeout(timeout);
      }, 100);
    }

    trackCardClickedStoreV2(data?.title, idx + 1);
  };

  return (
    <div
      className={s.root}
      ref={rootRef}
      onClick={(e) =>
        handleItemClickWithDelegation(
          e,
          CARDID,
          LayoutEntityType.Product,
          data?.data,
          data?.title
        )
      }
    >
      <div className={s.title}>
        <Typography label={data?.title} variant="label_1_bold" />
      </div>
      <div className={cn(s.tabBar, 'cmn_hide_scrollbar')}>
        {data?.metaData?.categories?.map((item, idx) => (
          <div
            key={`${idx}^&*()`}
            onClick={() => {
              handleTabClick(idx);
            }}
            className={cn(s.tab, 'cmn_touchable_area', {
              [s.activeTab]: idx === activeTab,
            })}
            id={`${data?.layoutId}-${idx}-brandsTab&`}
          >
            <div className={s.coverImage}>
              <AnimatedImage src={item?.coverImage} alt='coverImage' />
            </div>
            <Typography
              label={item?.title}
              className={s.title}
              color={idx == activeTab ? 'primary' : 'secondary'}
              variant={idx == activeTab ? 'label_4_bold' : 'label_4_medium'}
            />
          </div>
        ))}
      </div>
      <div className={s.listWrapper}>
        <div className={cn(s.cardList, 'cmn_hide_scrollbar')}>
          {parentIdMap?.[data?.metaData?.categories?.[activeTab]?.productId]
            ?.slice(0, 6)
            .map((item, index) => (
              <div
                className={s.cardWrapper}
                key={item?.id}
                id={`${data?.layoutId}-productCard&`}
              >
                <NewLaunchCard
                  key={item?.id}
                  data={item}
                  id={CARDID}
                  index={getIndex(item.productId)}
                />
              </div>
            ))}
        </div>
      </div>
      <div className={s.view}>
        <Button
          label="View more deals"
          variant="secondary_color"
          rightIcon={
            <Icon name="ChevronRightRegular" color="brand" width={16} height={16} />
          }
          size="small"
          onClick={() => {
            trackCtaClickedStoreV2(
              data?.title,
              'view_more_deals',
              activeTab + 1
            );
            const activeCat = data?.metaData?.categories?.[activeTab];
            handleItemClick(
              data?.entityType,
              activeCat?.productId,
              activeCat?.filter
            );
          }}
        />
      </div>
    </div>
  );
};
